import React, { memo, Suspense } from 'react'
import { useLDClient } from 'launchdarkly-react-client-sdk'

import Spinner from '@divvy-web/skylab.spinner'

import { getEmailFromStorage, getEmailHashFromStorage } from '@sputnik/auth'
import { SputnikCanaryProvider } from '@sputnik/canary/SputnikCanaryProvider'

export interface WithExperimentParams {
  experiment: string
  inputId: string
  bucketingKey?: string
}

const suspendedResource = <T extends any>(promise?: Promise<T>) => {
  let status = 'pending'
  let result

  const suspender = promise
    ?.then((response) => {
      status = 'success'
      result = response
    })
    .catch((error) => {
      status = 'error'
      result = error
    })

  return {
    read() {
      if (status === 'pending') {
        throw suspender
      } else if (status === 'error') {
        throw result
      }
      return result
    },
  }
}

export const LocalExperimentProvider = memo<React.PropsWithChildren<WithExperimentParams>>(
  // eslint-disable-next-line react/prop-types
  ({ children, experiment, inputId }) => {
    const ldClient = useLDClient()

    const emailHash = getEmailHashFromStorage() ?? 'anonymous-user'
    const userEmail = getEmailFromStorage() ?? ''

    suspendedResource(
      ldClient?.identify({
        kind: 'multi',
        creditApplicant: {
          key: emailHash,
          email: userEmail,
          _meta: {
            privateAttributes: ['/email'],
          },
        },
        [experiment]: {
          key: inputId,
        },
      }),
    )

    return <SputnikCanaryProvider>{children}</SputnikCanaryProvider>
  },
  (oldProps, newProps) => oldProps.experiment === newProps.experiment && oldProps.inputId === newProps.inputId,
)

export default (props: React.PropsWithChildren<WithExperimentParams>) => (
  <Suspense fallback={<Spinner />}>
    <LocalExperimentProvider {...props} />
  </Suspense>
)
